import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { MapPin, CheckCircle, Check, X, Upload, Info, AlertTriangle } from 'lucide-react';

export default function TerritoiresModals({ ctx }) {
  const { territoires, dispositifs, pays, showModal, setShowModal, editingTerritoire, importDialogOpen, setImportDialogOpen, importData, setImportData, importStats, formData, setFormData, handleSave, handleToggleDispositif, handlePaysChange, confirmImport } = ctx;
  return (
    <>
      {/* Modal de création/édition */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                {editingTerritoire ? 'Modifier le Territoire' : 'Nouveau Territoire'}
              </CardTitle>
              <CardDescription>
                Gérer les territoires éligibles pour les différents dispositifs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nom et Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nom du territoire <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: GUADELOUPE"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: GP"
                    maxLength={3}
                  />
                </div>
              </div>

              {/* Pays */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Pays <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.pays_code}
                  onChange={(e) => handlePaysChange(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Sélectionner un pays --</option>
                  {pays.map(p => (
                    <option key={p.code} value={p.code}>{p.nom}</option>
                  ))}
                </select>
              </div>

              {/* Dispositifs */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Dispositifs applicables
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {dispositifs.map(dispositif => (
                    <div
                      key={dispositif}
                      onClick={() => handleToggleDispositif(dispositif)}
                      className={`cursor-pointer p-3 border rounded-lg transition-colors ${
                        formData.dispositifs.includes(dispositif)
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-slate-300 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          formData.dispositifs.includes(dispositif)
                            ? 'border-blue-600 bg-blue-600'
                            : 'border-slate-300'
                        }`}>
                          {formData.dispositifs.includes(dispositif) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm font-medium">{dispositif}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Description optionnelle du territoire..."
                />
              </div>

              {/* Statut Actif */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="actif"
                  checked={formData.actif}
                  onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
                  className="rounded border-slate-300"
                />
                <label htmlFor="actif" className="text-sm font-medium text-slate-700">
                  Territoire actif
                </label>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button
                  onClick={() => setShowModal(false)}
                  variant="outline"
                >
                  <X className="mr-2 h-4 w-4" />
                  Annuler
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {editingTerritoire ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Import Confirmation Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-600" />
              Confirmer l&apos;import des territoires
            </DialogTitle>
            <DialogDescription>
              Vérifiez les données avant de confirmer l&apos;import.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{importStats.total}</p>
                <p className="text-sm text-blue-800">Total détecté</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{importStats.valid}</p>
                <p className="text-sm text-green-800">Valides</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{importStats.errors.length}</p>
                <p className="text-sm text-red-800">Erreurs</p>
              </div>
            </div>

            {/* Errors */}
            {importStats.errors.length > 0 && (
              <Alert className="bg-red-50 border-red-200">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <p className="font-semibold mb-2">Erreurs détectées :</p>
                  <ul className="list-disc list-inside text-sm">
                    {importStats.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err}</li>
                    ))}
                    {importStats.errors.length > 5 && (
                      <li>... et {importStats.errors.length - 5} autres erreurs</li>
                    )}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Preview */}
            {importData && importData.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 p-3 border-b border-slate-200">
                  <p className="font-semibold text-sm text-slate-700">
                    Aperçu des {Math.min(importData.length, 5)} premiers territoires
                  </p>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="p-2 text-left">Nom</th>
                        <th className="p-2 text-left">Code</th>
                        <th className="p-2 text-left">Pays</th>
                        <th className="p-2 text-left">Dispositifs</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importData.slice(0, 5).map((t, idx) => (
                        <tr key={idx} className="border-t border-slate-200">
                          <td className="p-2 font-medium">{t.nom}</td>
                          <td className="p-2"><Badge className="bg-slate-700 text-white font-semibold">{t.code}</Badge></td>
                          <td className="p-2">{t.pays_nom || t.pays_code}</td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-1">
                              {(t.dispositifs || []).slice(0, 2).map((d, i) => (
                                <Badge key={i} className="bg-blue-700 text-white text-xs font-semibold">{d}</Badge>
                              ))}
                              {(t.dispositifs || []).length > 2 && (
                                <Badge className="bg-slate-600 text-white text-xs font-semibold">+{t.dispositifs.length - 2}</Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <Alert className="bg-amber-50 border-amber-200">
              <Info className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Les territoires avec un code existant seront mis à jour. Les nouveaux territoires seront créés.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              onClick={() => {
                setImportDialogOpen(false);
                setImportData(null);
              }} 
              variant="outline"
            >
              Annuler
            </Button>
            <Button 
              onClick={confirmImport}
              className="bg-green-600 hover:bg-green-700"
              disabled={!importData || importData.length === 0}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Importer {importStats.valid} territoire(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
